import { Col, Flex, Reveal } from '@landsbankinn/ui-kit';
import { useEffect, useState } from 'react';
import { useMarketingAreaContext } from 'src/components/Layout/Desktop/MarketingContent/MarketingAreaContext';
import { setPreviousLoginType } from 'src/components/Login//utils';
import { useScreenSize } from 'src/hooks/useScreenSize';
import { AppHubStateType, type AppHubUser } from 'src/services/AppHubService';
import { AllowedLoginType, useApplicationClientContext } from 'src/state';
import {
  getAppHubStateDebugUrl,
  useSetDebugUrl,
} from '../../hooks/useSetDebugUrl';
import { useAppService } from './AppContext';
import { AppLoginQR } from './AppLoginQR';
import { AppLoginUsers } from './AppLoginUsers';
import { AppPendingInternal } from './AppPending';
import { AppQR } from './AppQR';

export function App() {
  const [isPersistent, setIsPersistent] = useState(false);
  const [isUsingMouse, setIsUsingMouse] = useState(false);
  const {
    state,
    setShowQrCode,
    authenticateUser,
    revokeUser,
    fetchQrCode,
    setPersistanceOptIn,
  } = useAppService();
  const { isMobile, isXL } = useScreenSize();
  const [currentStep, setCurrentStep] = useState<
    'qr' | 'users' | 'pending' | undefined
  >(undefined);
  const { start } = useAppService();
  const [clientState, clientDispatch] = useApplicationClientContext();

  const { setCustomComponent: setMarketingArea } = useMarketingAreaContext();

  useSetDebugUrl(getAppHubStateDebugUrl(state));

  // re-enable focus styling when any key is pressed
  document.body.addEventListener('keydown', function () {
    if (isUsingMouse) setIsUsingMouse(false);
  });

  const handleRememberMe = (checked: boolean): void => {
    setIsPersistent(checked);
    setPersistanceOptIn(checked);
    setIsUsingMouse(true);
  };

  const handleAuthenticateUser = (user: AppHubUser): void => {
    authenticateUser(user);
  };

  const handleRevokeUser = (user: AppHubUser): void => {
    revokeUser(user);
  };

  const handleSwitchToQrCode = (): void => {
    if (state?.type === AppHubStateType.Login && state.qr === undefined) {
      fetchQrCode();
    }
    setCurrentStep('qr');
    setShowQrCode(true);
  };

  const handleSwitchToUsers = (): void => {
    if (state?.type === AppHubStateType.Login && state.users !== undefined) {
      setCurrentStep('users');
      setShowQrCode(false);
    }
  };

  useEffect(() => {
    if (state !== undefined && state.type === AppHubStateType.Login) {
      // Calculate the initial step based on the state
      if (currentStep === undefined) {
        const shouldShowQR = state.qr !== undefined;

        // Only show users if we have users and we are not on mobile.
        // Mobile users will always see a button to login.
        const shouldShowUsers =
          state.users !== undefined &&
          state.users.length > 0 &&
          isMobile === false;

        // This is a special edge case where users has saved users and no qr code.
        // The user resizes the window to mobile so we need to fetch a qr code.
        if (
          shouldShowQR === false &&
          shouldShowUsers === false &&
          state.qr === undefined &&
          state.users !== undefined &&
          state.users.length > 0
        ) {
          fetchQrCode();
        }

        if (currentStep !== 'users' && shouldShowUsers) {
          setCurrentStep('users');
          setShowQrCode(false);
        } else if (
          currentStep !== 'qr' &&
          (shouldShowQR ||
            (shouldShowQR === false && shouldShowUsers === false))
        ) {
          setCurrentStep('qr');
          setShowQrCode(true);
        }
      } else if (currentStep === 'users') {
        if (state.users === undefined || state.users.length === 0 || isMobile) {
          setCurrentStep('qr');
          setShowQrCode(true);
        }
      }
    } else if (state !== undefined && state.type === AppHubStateType.Pending) {
      setCurrentStep('pending');
    }
  }, [
    clientState,
    clientDispatch,
    currentStep,
    state,
    setShowQrCode,
    isMobile,
    fetchQrCode,
  ]);

  useEffect(() => {
    start();
    setPreviousLoginType(AllowedLoginType.App);
  }, [start]);

  useEffect(() => {
    if ((isXL && currentStep === 'qr') || currentStep === 'pending') {
      // Only show the QR code on XL screens during QR step
      setMarketingArea(<AppQR isMobile={isMobile} />);
    } else {
      // Remove the custom component from the marketing area
      // when not on the QR step or on non XL screens
      setMarketingArea(undefined);
    }

    return () => {
      // On cleanup, remove the custom component from the marketing area
      setMarketingArea(undefined);
    };
  }, [currentStep, isMobile, isXL, setMarketingArea]);

  return (
    <Col grow="1">
      {state && (
        <>
          {currentStep === 'users' &&
            state.type === 'login' &&
            state.users &&
            state.users.length > 0 && (
              <AppLoginUsers
                isMobile={isMobile}
                users={state.users}
                onAuthenticateUser={handleAuthenticateUser}
                onRevokeUser={handleRevokeUser}
                onQR={handleSwitchToQrCode}
              />
            )}
          {currentStep === 'qr' && state.type === 'login' && state.qr && (
            <AppLoginQR
              isMobile={isMobile}
              isXL={isXL}
              isPersistent={isPersistent}
              isUsers={state.users !== undefined && state.users.length > 0}
              onRememberMe={handleRememberMe}
              onUsers={handleSwitchToUsers}
            />
          )}
          {currentStep === 'pending' && (
            <Reveal
              show={true}
              initial={isXL === true}
              animationDelay={900}
              animationDuration={600}
              skipHeightAnimation
              startHeightAnimationAt={0}
            >
              <Flex
                height="100%"
                grow="1"
                direction="col"
                mainAxisAlignment="center"
              >
                <AppPendingInternal />
              </Flex>
            </Reveal>
          )}
        </>
      )}
    </Col>
  );
}
